import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ScriptProvider } from './context/ScriptContext';
import ScrollToTop from './components/ScrollToTop';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import LiturgySchedule from './components/LiturgySchedule';
import NewsPreview from './components/NewsPreview';
import PostCards from './components/PostCards';
import MapSection from './components/MapSection';
import Footer from './components/Footer';
import AdminBadge from './components/AdminBadge';
import AllNewsPage from './pages/AllNewsPage';
import SingleNewsPage from './pages/SingleNewsPage';
import NewsEditPage from './pages/NewsEditPage';
import AllGalleryPage from './pages/AllGalleryPage';
import GalleryEditPage from './pages/GalleryEditPage';
import AllDeliveriesPage from './pages/AllDeliveriesPage';
import DeliveriesEditPage from './pages/DeliveriesEditPage';
import OrdersAdminPage from './pages/OrdersAdminPage';
import AdminLoginPage from './pages/AdminLoginPage';
import NotFoundPage from './pages/NotFoundPage';
import SiteModePage from './pages/SiteModePage';

import CategoryPage from './pages/CategoryPage';
import CategoryEditPage from './pages/CategoryEditPage';
import VisitPage from './pages/VisitPage';
import FrescoesPage from './pages/FrescoesPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import CookiePolicyPage from './pages/CookiePolicyPage';
import TermsOfUsePage from './pages/TermsOfUsePage';
import CreditsPage from './pages/CreditsPage';
import { getSetting } from './services/api';
import { useDocumentTitle } from './hooks/useDocumentTitle';
import './App.css';
import './About.css';

function HomePage() {
  useDocumentTitle('Манастир Градац  — Задужбина краљице Јелене Анжујске', '/');
  return (
    <>
      <Hero />
      <LiturgySchedule />
      <div className="cross-separator" role="separator" aria-hidden="true">{'\u2626'}</div>
      <NewsPreview />
      <div className="cross-separator" role="separator" aria-hidden="true">{'\u2626'}</div>
      <PostCards />
      <div className="cross-separator" role="separator" aria-hidden="true">{'\u2626'}</div>
      <MapSection />
    </>
  );
}

/**
 * AppContent is inside AuthProvider so it can use useAuth().
 * It checks site_mode on mount and renders the appropriate page.
 */
function AppContent() {
  const { isAdmin } = useAuth();
  const [siteMode, setSiteMode] = useState<string>('');
  const [hideProcurements, setHideProcurements] = useState<boolean>(false);
  const [hideUsefulTexts, setHideUsefulTexts] = useState<boolean>(false);
  const [loading, setLoading] = useState(true);

  // Load settings from backend on mount
  useEffect(() => {
    Promise.all([
      getSetting('site_mode').catch(() => ({ value: '' })),
      getSetting('hide_procurements').catch(() => ({ value: 'false' })),
      getSetting('hide_useful_texts').catch(() => ({ value: 'false' }))
    ]).then(([modeRes, procRes, textsRes]) => {
      setSiteMode(modeRes.value || '');
      setHideProcurements(procRes.value === 'true');
      setHideUsefulTexts(textsRes.value === 'true');
    }).finally(() => setLoading(false));
  }, []);

  // Callback for AdminBadge to update siteMode in real-time after save
  const handleSiteModeChange = useCallback((mode: string) => {
    setSiteMode(mode);
  }, []);

  // Callback for AdminBadge to update hideProcurements in real-time
  const handleProcurementsChange = useCallback((hide: boolean) => {
    setHideProcurements(hide);
  }, []);

  const handleUsefulTextsChange = useCallback((hide: boolean) => {
    setHideUsefulTexts(hide);
  }, []);

  // Don't flash content while loading
  if (loading) return null;

  // If site is in maintenance/coming_soon mode and user is NOT admin, show the mode page
  const isMaintenanceMode = siteMode === 'maintenance' || siteMode === 'coming_soon';
  if (isMaintenanceMode && !isAdmin) {
    return (
      <SiteModePage mode={siteMode as 'coming_soon' | 'maintenance'} />
    );
  }

  return (
    <BrowserRouter>
      <ScriptProvider>
        <ScrollToTop />
        <a href="#main" className="skip-link">Прескочи на садржај</a>
        <Navbar hideProcurements={hideProcurements} hideUsefulTexts={hideUsefulTexts} />
        <main id="main">
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/poseta" element={<VisitPage />} />
            <Route path="/freskopis" element={<FrescoesPage />} />
            
            <Route path="/novosti/nova" element={<NewsEditPage />} />
            <Route path="/novosti/:id/uredi" element={<NewsEditPage />} />
            <Route path="/novosti" element={<AllNewsPage />} />
            <Route path="/novosti/:id" element={<SingleNewsPage />} />
            
            <Route path="/galerija/nova" element={<GalleryEditPage />} />
            <Route path="/galerija/:id/uredi" element={<GalleryEditPage />} />
            <Route path="/galerija" element={<AllGalleryPage />} />
            
            {!hideProcurements && (
              <>
                <Route path="/isporuke/nova" element={<DeliveriesEditPage />} />
                <Route path="/isporuke/:id/uredi" element={<DeliveriesEditPage />} />
                <Route path="/isporuke" element={<AllDeliveriesPage />} />
              </>
            )}
            
            <Route path="/narudzbine" element={<OrdersAdminPage />} />
            <Route path="/admin" element={<AdminLoginPage />} />
            <Route path="/politika-privatnosti" element={<PrivacyPolicyPage />} />
            <Route path="/kolacici" element={<CookiePolicyPage />} />
            <Route path="/uslovi-koriscenja" element={<TermsOfUsePage />} />
            <Route path="/zasluge" element={<CreditsPage />} />

            {/* Dynamic slug routes */}
            <Route path="/:slug" element={<CategoryPage hideUsefulTexts={hideUsefulTexts} />} />
            <Route path="/:slug/uredi" element={<CategoryEditPage />} />
            
            {/* Catch-all 404 */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </main>
        <Footer hideProcurements={hideProcurements} hideUsefulTexts={hideUsefulTexts} />
        <AdminBadge 
          onSiteModeChange={handleSiteModeChange} 
          onProcurementsChange={handleProcurementsChange} 
          onUsefulTextsChange={handleUsefulTextsChange}
        />
      </ScriptProvider>
    </BrowserRouter>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
